import React, {
    useEffect,
    useRef,
    useState
} from "react";

import "./Chatbot.css";
import useSelfcareAssistant from "./useSelfcareAssistant";
import { VOICE_API_BASE_URL, downloadAssistantDocument } from "./selfcareClient";

// ============================================================
// MESSAGE
// ============================================================

const Message = ({
                     text,
                     sender,
                     time
                 }) => (

    <div
        className={`message ${sender}`}
    >

        <div className="message-avatar">

            {
                sender === "bot"
                    ? "🤖"
                    : "👤"
            }

        </div>


        <div>

            <div
                className="message-content"
                dir="auto"
            >

                {text}

            </div>


            <div className="message-time">

                {time}

            </div>

        </div>

    </div>
);


// ============================================================
// SUGGESTIONS
// ============================================================

const Suggestions = ({
                         suggestions,
                         onSendSuggestion
                     }) => (

    <div className="suggestions">

        {
            suggestions.map(
                (
                    suggestion,
                    index
                ) => (

                    <button
                        key={index}

                        className="suggestion-btn"

                        onClick={() =>
                            onSendSuggestion(
                                suggestion.text
                            )
                        }
                    >

                        {suggestion.icon}

                        {" "}

                        {suggestion.text}

                    </button>

                )
            )
        }

    </div>
);


// ============================================================
// TYPING
// ============================================================

const TypingIndicator = () => (

    <div className="message bot typing-indicator active">

        <div className="message-avatar">
            🤖
        </div>

        <div className="typing-dots">

            <div className="typing-dot" />
            <div className="typing-dot" />
            <div className="typing-dot" />

        </div>

    </div>
);


// ============================================================
// INITIAL SUGGESTIONS
// ============================================================

const initialSuggestions = [
    { text: "Quelles attestations sont disponibles ?", icon: "📄" },
    { text: "Je veux une attestation", icon: "✍" },
    { text: "Mes demandes d'attestation", icon: "📋" },
    { text: "Comment demander une attestation ?", icon: "❓" },
];


// ============================================================
// CHATBOT
// ============================================================

function Chatbot() {

    const assistant = useSelfcareAssistant(() => { setMessages([]); setInput(""); });
    const sendingRef = useRef(false);

    // ========================================================
    // STATES
    // ========================================================

    const [
        isOpen,
        setIsOpen
    ] = useState(false);


    const [
        isMaximized,
        setIsMaximized
    ] = useState(false);


    const [
        input,
        setInput
    ] = useState("");


    const [
        isTyping,
        setIsTyping
    ] = useState(false);


    const [
        isRecording,
        setIsRecording
    ] = useState(false);


    const [
        isTranscribing,
        setIsTranscribing
    ] = useState(false);


    const [
        messages,
        setMessages
    ] = useState([

        {
            text:
                "Bonjour ! Je suis votre assistant RH virtuel. Comment puis-je vous aider aujourd'hui ?",

            sender:
                "bot",

            time:
                new Date()
                    .toLocaleTimeString(
                        "fr-FR",
                        {
                            hour:
                                "2-digit",

                            minute:
                                "2-digit"
                        }
                    ),

            isInitial:
                true
        }

    ]);


    // ========================================================
    // REFS
    // ========================================================

    const messagesEndRef =
        useRef(null);


    const mediaRecorderRef =
        useRef(null);


    const audioChunksRef =
        useRef([]);


    const audioStreamRef =
        useRef(null);


    // ========================================================
    // CLEAN MICRO
    // ========================================================

    useEffect(
        () => {

            return () => {

                audioStreamRef.current
                    ?.getTracks()
                    .forEach(
                        (
                            track
                        ) =>
                            track.stop()
                    );

            };

        },
        []
    );


    // ========================================================
    // SCROLL
    // ========================================================

    const scrollToBottom =
        () => {

            messagesEndRef.current
                ?.scrollIntoView({
                    behavior:
                        "smooth"
                });

        };


    useEffect(
        () => {

            if (isOpen) {

                scrollToBottom();

            }

        },
        [
            messages,
            isTyping,
            isOpen
        ]
    );


    // ========================================================
    // ADD MESSAGE
    // ========================================================

    const addMessage =
        (
            text,
            sender
        ) => {

            const time =
                new Date()
                    .toLocaleTimeString(
                        "fr-FR",
                        {
                            hour:
                                "2-digit",

                            minute:
                                "2-digit"
                        }
                    );


            setMessages(
                (
                    previous
                ) => [

                    ...previous,

                    {
                        text,
                        sender,
                        time
                    }
                ]
            );

        };


    const sendMessage = async (messageText = input) => {
        const text = String(messageText || "").trim();
        if (!text || sendingRef.current) return;
        sendingRef.current = true;
        addMessage(text, "user");
        setInput("");
        setIsTyping(true);
        try {
            const result = await assistant.send(text);
            if (result) {
                addMessage(result.message, "bot");
                if (result.downloadAvailable) {
                    const id = result.conversation.conversationId;
                    addMessage(<button type="button" onClick={async () => {
                        try { await downloadAssistantDocument(id, result.downloadId); }
                        catch (error) { addMessage(error.message, "bot"); }
                    }}>Télécharger le PDF</button>, "bot");
                }
            }
        } catch (error) {
            addMessage(error.message || "Le traitement n'a pas été confirmé. Consultez votre historique avant de recommencer.", "bot");
        } finally {
            sendingRef.current = false;
            setIsTyping(false);
        }
    };

    const stopAudioStream =
        () => {

            audioStreamRef.current
                ?.getTracks()
                .forEach(
                    (
                        track
                    ) =>
                        track.stop()
                );


            audioStreamRef.current =
                null;

        };


    // ========================================================
    // BUILD DYNAMIC HOTWORDS
    // ========================================================

    // ========================================================
    // TRANSCRIBE
    // ========================================================

    const transcribeRecording =
        async (
            audioBlob
        ) => {

            setIsTranscribing(
                true
            );


            try {

                const formData =
                    new FormData();


                formData.append(
                    "audio",
                    audioBlob,
                    "recording.webm"
                );


                const response =
                    await fetch(

                        `${VOICE_API_BASE_URL}/transcribe`,

                        {
                            method:
                                "POST",

                            body:
                            formData
                        }
                    );


                const data =
                    await response.json();


                if (
                    !response.ok
                    ||
                    !data.transcription
                ) {

                    throw new Error(
                        data.error
                        ||
                        "Transcription impossible"
                    );

                }


                await sendMessage(
                    data.transcription
                );

            }

            catch (
                error
                ) {


                addMessage(
                    `Erreur de transcription : ${error.message}`,
                    "bot"
                );

            }

            finally {

                setIsTranscribing(
                    false
                );

            }

        };


    // ========================================================
    // RECORD
    // ========================================================

    const toggleRecording =
        async () => {

            // =================================================
            // STOP RECORDING
            // =================================================

            if (
                isRecording
            ) {

                mediaRecorderRef.current
                    ?.stop();


                setIsRecording(
                    false
                );


                return;

            }


            // =================================================
            // START
            // =================================================

            try {

                const stream =
                    await navigator
                        .mediaDevices
                        .getUserMedia({

                            audio: {

                                echoCancellation:
                                    true,

                                noiseSuppression:
                                    true,

                                autoGainControl:
                                    true,

                                channelCount:
                                    1
                            }
                        });


                let recorder;


                // =============================================
                // Chrome / Edge
                // =============================================

                if (
                    MediaRecorder.isTypeSupported(
                        "audio/webm;codecs=opus"
                    )
                ) {

                    recorder =
                        new MediaRecorder(
                            stream,
                            {
                                mimeType:
                                    "audio/webm;codecs=opus"
                            }
                        );

                }

                else {

                    recorder =
                        new MediaRecorder(
                            stream
                        );

                }


                audioStreamRef.current =
                    stream;


                audioChunksRef.current =
                    [];


                mediaRecorderRef.current =
                    recorder;


                recorder.ondataavailable =
                    (
                        event
                    ) => {

                        if (
                            event.data
                            &&
                            event.data.size > 0
                        ) {

                            audioChunksRef.current
                                .push(
                                    event.data
                                );

                        }

                    };


                recorder.onstop =
                    () => {

                        const audioBlob =
                            new Blob(

                                audioChunksRef.current,

                                {
                                    type:
                                        recorder.mimeType
                                        ||
                                        "audio/webm"
                                }
                            );


                        stopAudioStream();


                        if (
                            audioBlob.size === 0
                        ) {

                            addMessage(
                                "Aucun son n'a été enregistré.",
                                "bot"
                            );

                            return;

                        }


                        transcribeRecording(
                            audioBlob
                        );

                    };


                recorder.start();


                setIsRecording(
                    true
                );

            }

            catch (
                error
                ) {


                stopAudioStream();


                addMessage(
                    "Impossible d'accéder au microphone. Vérifiez son autorisation dans le navigateur.",
                    "bot"
                );

            }

        };


    // ========================================================
    // ENTER
    // ========================================================

    const handleKeyDown =
        (
            event
        ) => {

            if (
                event.key === "Enter"
            ) {

                event.preventDefault();


                sendMessage();

            }

        };


    // ========================================================
    // OPEN
    // ========================================================

    const toggleChat =
        () => {

            setIsOpen(
                (
                    previous
                ) =>
                    !previous
            );


            setIsMaximized(
                false
            );

        };


    // ========================================================
    // MAXIMIZE
    // ========================================================

    const toggleMaximize =
        () => {

            setIsMaximized(
                (
                    previous
                ) =>
                    !previous
            );

        };


    // ========================================================
    // JSX
    // ========================================================

    return (

        <>

            {/* =============================================== */}
            {/* FLOAT BUTTON */}
            {/* =============================================== */}

            <div
                className={
                    `chatbot-float-icon ${
                        isOpen
                            ? "hidden"
                            : ""
                    }`
                }

                onClick={
                    toggleChat
                }
            >

                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >

                    <path
                        d="M12 2C6.48 2 2 6.48 2 12C2 13.54 2.38 14.98 3.06 16.26L2 22L7.74 20.94C9.02 21.62 10.46 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C10.69 20 9.45 19.67 8.36 19.09L8 18.91L4.55 19.82L5.46 16.37L5.28 16.01C4.7 14.92 4.37 13.68 4.37 12.37C4.37 7.97 7.97 4.37 12.37 4.37C16.77 4.37 20.37 7.97 20.37 12.37C20.37 16.77 16.63 20 12 20Z"
                        fill="white"
                    />

                    <circle
                        cx="9"
                        cy="12"
                        r="1"
                        fill="white"
                    />

                    <circle
                        cx="12"
                        cy="12"
                        r="1"
                        fill="white"
                    />

                    <circle
                        cx="15"
                        cy="12"
                        r="1"
                        fill="white"
                    />

                </svg>


                <div
                    className="chatbot-pulse-ring"
                />

            </div>


            {/* =============================================== */}
            {/* CHAT */}
            {/* =============================================== */}

            {
                isOpen
                &&
                (

                    <>

                        {
                            isMaximized
                            &&
                            (

                                <div
                                    className="chatbot-modal-overlay"

                                    onClick={
                                        toggleMaximize
                                    }
                                />

                            )
                        }


                        <div
                            className={
                                `chat-container ${
                                    isMaximized
                                        ? "maximized"
                                        : "minimized"
                                }`
                            }
                        >


                            {/* ================================= */}
                            {/* HEADER */}
                            {/* ================================= */}

                            <div
                                className="chat-header"
                            >

                                <div
                                    className="avatar"
                                >
                                    🤖
                                </div>


                                <div
                                    className="header-info"
                                >

                                    <h2>
                                        Assistant RH
                                    </h2>

                                </div>


                                <button
                                    className="control-btn maximize-btn"

                                    onClick={
                                        toggleMaximize
                                    }

                                    title={
                                        isMaximized
                                            ? "Réduire"
                                            : "Agrandir"
                                    }
                                >

                                    {
                                        isMaximized
                                            ? "↙"
                                            : "↗"
                                    }

                                </button>


                                <button
                                    className="control-btn close-btn"

                                    onClick={
                                        toggleChat
                                    }
                                >
                                    ✕
                                </button>

                            </div>


                            {/* ================================= */}
                            {/* MESSAGES */}
                            {/* ================================= */}

                            <div
                                className="chat-messages"
                            >

                                {
                                    messages.map(
                                        (
                                            message,
                                            index
                                        ) => (

                                            <React.Fragment
                                                key={index}
                                            >

                                                <Message
                                                    text={
                                                        message.text
                                                    }

                                                    sender={
                                                        message.sender
                                                    }

                                                    time={
                                                        message.time
                                                    }
                                                />


                                                {
                                                    message.isInitial
                                                    &&
                                                    message.sender === "bot"
                                                    &&
                                                    (

                                                        <Suggestions

                                                            suggestions={
                                                                initialSuggestions
                                                            }

                                                            onSendSuggestion={
                                                                sendMessage
                                                            }
                                                        />

                                                    )
                                                }

                                            </React.Fragment>

                                        )
                                    )
                                }


                                {
                                    isTyping
                                    &&
                                    (
                                        <TypingIndicator />
                                    )
                                }


                                <div
                                    ref={
                                        messagesEndRef
                                    }
                                />

                            </div>


                            {/* ================================= */}
                            {/* INPUT */}
                            {/* ================================= */}

                            <div
                                className="chat-input-area"
                            >

                                <div className="assistant-controls">
                                    <button type="button" disabled={isTyping} onClick={async () => {
                                        try { const result = await assistant.resume(); if (result) addMessage(result.message, "bot"); }
                                        catch (error) { addMessage(error.message, "bot"); }
                                    }}>Reprendre</button>
                                    <button type="button" disabled={isTyping} onClick={() => {
                                        assistant.reset();
                                        addMessage("Nouvelle conversation. Les demandes déjà enregistrées restent dans Mes attestations.", "bot");
                                    }}>Nouvelle conversation</button>
                                    <label>Pièces jointes
                                        <input type="file" multiple disabled={isTyping}
                                            onChange={event => assistant.setFiles(Array.from(event.target.files || []))} />
                                    </label>
                                    {assistant.files.length > 0 && <span>{assistant.files.length} fichier(s) sélectionné(s)</span>}
                                </div>
                                <div
                                    className="input-wrapper"
                                >

                                    <input
                                        type="text"

                                        id="userInput"

                                        dir="auto"

                                        placeholder={

                                            isRecording

                                                ? "Écoute en cours..."

                                                : isTranscribing

                                                    ? "Transcription en cours..."

                                                    : "Tapez votre message..."
                                        }

                                        value={
                                            input
                                        }

                                        onChange={
                                            (
                                                event
                                            ) =>
                                                setInput(
                                                    event.target.value
                                                )
                                        }

                                        onKeyDown={
                                            handleKeyDown
                                        }

                                        disabled={
                                            isRecording
                                            ||
                                            isTranscribing
                                        }
                                    />


                                    {/* MICRO */}

                                    <button
                                        type="button"

                                        className="icon-btn"

                                        onClick={
                                            toggleRecording
                                        }

                                        disabled={
                                            isTranscribing
                                        }

                                        title={
                                            isRecording
                                                ? "Arrêter l'enregistrement"
                                                : "Parler"
                                        }

                                        aria-label={
                                            isRecording
                                                ? "Arrêter l'enregistrement"
                                                : "Démarrer l'enregistrement"
                                        }

                                        style={{

                                            flexShrink:
                                                0,

                                            background:
                                                isRecording
                                                    ? "#dc2626"
                                                    : "#e2e8f0",

                                            color:
                                                isRecording
                                                    ? "#fff"
                                                    : "#2f4595"
                                        }}
                                    >

                                        {
                                            isRecording
                                                ? "■"
                                                : "🎙"
                                        }

                                    </button>


                                    {/* SEND */}

                                    <button
                                        className="icon-btn send-btn"

                                        onClick={() =>
                                            sendMessage()
                                        }

                                        disabled={
                                            isTyping
                                            || !input.trim()
                                            ||
                                            isRecording
                                            ||
                                            isTranscribing
                                        }
                                    >

                                        ➤

                                    </button>

                                </div>

                            </div>

                        </div>

                    </>

                )
            }

        </>

    );

}


export default Chatbot;
